import React, { useEffect, useState } from "react";
import { Typography, Divider } from "@mui/material";
import { Box } from "@mui/system";
import toast from "react-hot-toast";

import { useDrawer } from "@/components/Drawer/context/DrawerContext";
import AuthService from "@/services/api/auth/auth";
import { formttedDate } from "@/lib/hyphen";

interface Props {
  retailerId: any;
}

const InfoField = ({
  label,
  value,
}: {
  label: string;
  value?: any;
}) => (
  <Typography>
    <strong>{label}</strong>
    <p>{value || "-"}</p>
  </Typography>
);

const Grid3 = ({ children }: { children: React.ReactNode }) => (
  <Box display="grid" sx={{
      gridTemplateColumns: {
        xs: "1fr 1fr",  
        sm: "1fr 1fr",
        md: "1fr 1fr 1fr", 
      },
    }} gap={2}>
    {children}
  </Box>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box mt={3}>
    <Typography variant="h6" fontWeight={600} className="bg-[#6570FC] p-3 md:p-2 text-white rounded-xl">
      {title}
    </Typography>
    <Divider sx={{ my: 1 }} />
    {children}
  </Box>
);

const RetailerDetailsDrawer: React.FC<Props> = ({ retailerId }) => {
  const { closeDrawer } = useDrawer();
  const [retailerData, setRetailerData] = useState<any>(null);

  const fetchRetailerDetails = async () => {
    try {
      const res = await AuthService.GetSpecificRetailer(retailerId);
      let obj = {}; 
      res?.data?.map((item: any) => {
        obj = item;
      }) || []; 
      setRetailerData(obj);
    } catch (error) {
      console.error("Retailer fetch failed", error);
      toast.error("Failed to fetch retailer details");
    }
  };
  useEffect(() => {
    if (!retailerId) return;
    fetchRetailerDetails();
  }, [retailerId]);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Retailer Details
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Basic Info */}
      <Grid3>
        <InfoField label="Retailer Name" value={retailerData?.name} />
        <InfoField
          label="Created Date"
          value={formttedDate(retailerData?.createdDate)}
        />
        <InfoField
          label="Modified Date"
          value={formttedDate(retailerData?.modifiedDate)}
        />
      </Grid3>

      {/* Primary Contact */}
      <Section title="Retailer Primary Contact">
        <Grid3>
          <InfoField label="Person Name" value={retailerData?.contactName} />
          <InfoField label="Mobile Number" value={retailerData?.mobileNumber} />
          <InfoField label="Email Id" value={retailerData?.emailId} />
          <InfoField label="Designation" value={retailerData?.designation} />
        </Grid3>
      </Section>

      {/* Address */}
      <Section title="Retailer Address">
        <Box mb={2}>
          <InfoField label="Address" value={retailerData?.address} />
        </Box>

        <Grid3>
          <InfoField label="Country" value={retailerData?.countryName} />
          <InfoField label="State" value={retailerData?.stateName} />
          <InfoField label="City" value={retailerData?.cityName} />
          <InfoField label="Pincode" value={retailerData?.pincode} />
        </Grid3>
      </Section>

      {/* Other Info */}
      <Section title="Other Info">
        <Grid3>
          <InfoField label="Status" value={retailerData?.status} />
          {retailerData?.status === "Reject" && (
            <InfoField
              label="Rejected Reason"
              value={retailerData?.rejectedReason}
            />
          )}
        </Grid3>
      </Section>
    </Box>
  );
};

export default RetailerDetailsDrawer;
